import { useState, useEffect, useRef, useCallback } from "react";
import * as sync from "../lib/sync.js";
import { loadGis } from "../lib/gis.js";

/**
 * Compact sign-in for the app header.
 *
 * Signed out it renders Google's own button — their branding terms require it,
 * and it is the only supported way to obtain an ID token. Signed in it collapses
 * to a chip that jumps to Settings, where the sync controls live; duplicating
 * save/load up here would give two places to press for the same thing.
 *
 * State is shared with the Settings panel through sync.onAccountChange rather
 * than lifted through App.jsx, so signing in from either place updates both.
 */
export default function HeaderAccount({ clientId, isMobile, onToast, onOpenSettings }) {
  const [meta, setMeta] = useState(null);
  const [ready, setReady] = useState(false);
  const buttonRef = useRef(null);

  const refresh = useCallback(async () => {
    const signedIn = await sync.isSignedIn();
    setMeta(signedIn ? (await sync.getMeta()) || {} : null);
    setReady(true);
  }, []);

  useEffect(() => {
    refresh();
    return sync.onAccountChange(refresh);
  }, [refresh]);

  const handleCredential = useCallback(
    async (response) => {
      try {
        const m = await sync.signIn(response.credential);
        onToast?.(`Signed in as ${m.email || "your account"}`);
      } catch (e) {
        onToast?.(e.message, "error");
      }
    },
    [onToast]
  );

  useEffect(() => {
    // Nothing to render until we know the signed-out state for certain, or the
    // button would flash in for signed-in users on every load.
    if (!ready || meta || !clientId || !buttonRef.current) return;
    let cancelled = false;
    loadGis()
      .then(() => {
        if (cancelled || !buttonRef.current) return;
        window.google.accounts.id.initialize({ client_id: clientId, callback: handleCredential });
        window.google.accounts.id.renderButton(buttonRef.current, {
          theme: "filled_black",
          size: "medium",
          type: isMobile ? "icon" : "standard",
          text: "signin",
          shape: "pill",
        });
      })
      .catch(() => {
        // Blocked or offline. The Settings panel reports the detail; the header
        // stays quiet rather than putting an error in the masthead.
      });
    return () => {
      cancelled = true;
    };
  }, [ready, meta, clientId, isMobile, handleCredential]);

  if (!clientId || !ready) return null;

  if (meta) {
    const label = meta.email || meta.name || "Account";
    const initial = (label[0] || "?").toUpperCase();
    return (
      <button
        onClick={onOpenSettings}
        title={`Signed in as ${label} — open sync settings`}
        aria-label={`Signed in as ${label}. Open sync settings`}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          padding: isMobile ? 3 : "4px 10px 4px 4px",
          borderRadius: 999,
          border: "1px solid rgba(90,185,90,0.35)",
          background: "rgba(90,185,90,0.10)",
          cursor: "pointer",
          fontFamily: "inherit",
          maxWidth: isMobile ? 34 : 190,
        }}
      >
        <span
          style={{
            width: 24,
            height: 24,
            flexShrink: 0,
            borderRadius: "50%",
            background: "#5cb85c",
            color: "#06180d",
            fontSize: 12,
            fontWeight: 800,
            display: "grid",
            placeItems: "center",
          }}
        >
          {initial}
        </span>
        {!isMobile && (
          <span
            style={{
              fontSize: 11.5,
              color: "var(--text-soft2)",
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </span>
        )}
      </button>
    );
  }

  return <div ref={buttonRef} style={{ display: "flex", minHeight: 32 }} />;
}
